// Single source of truth for the product catalogue as crawlers and AI assistants see it.
// Used by Components/seo/ProductSeo (SoftwareApplication + breadcrumb schema), by the
// blog detail page (post-to-product links) and mirrored in public/llms.txt. Keep the
// descriptions free of em and en dashes; they end up in meta tags and llms.txt verbatim.
//
// `path` is the page in this app; `url` is set instead for products hosted on their own
// subdomain. `keywords` are matched against a blog post's title, tags and categories.

export const SITE_URL = (process.env.NEXT_PUBLIC_NEXTAUTH_URL || 'https://cloudastra.co').replace(
    /\/$/,
    '',
)

// The one logo URL used in structured data and social tags. The hash is the asset's build
// hash; update it here only.
export const LOGO_URL = `${SITE_URL}/_next/image?url=%2F_next%2Fstatic%2Fmedia%2FCloudastra-Logo-big.9ffccf57.png&w=828&q=75`

export const PRODUCTS = {
    debtpulse: {
        key: 'debtpulse',
        name: 'DebtPulse',
        path: '/products/debtpulse',
        title: 'DebtPulse: AI-Powered Digital Collections Platform | Cloudastra',
        description:
            'AI-powered digital collections platform for NBFCs and fintech lenders in India. Prioritize high-risk accounts, automate compliant outreach and improve recovery rates at scale.',
        category: 'FinanceApplication',
        keywords: ['debt collection', 'collections', 'nbfc', 'lending', 'loan', 'debt recovery', 'dpd', 'borrower'],
    },
    secureFlow: {
        key: 'secureFlow',
        name: 'SecureFlow',
        path: '/products/secure-flow',
        title: 'SecureFlow: AI-Powered Fraud Prevention and Payment Security | Cloudastra',
        description:
            'High-performance fraud detection pipeline that scores millions of payment transactions in real time with sub-millisecond latency, keeping revenue safe around the clock.',
        category: 'FinanceApplication',
        keywords: ['fraud', 'payment security', 'transaction monitoring', 'chargeback', 'payments'],
    },
    riskIntel: {
        key: 'riskIntel',
        name: 'RiskIntel',
        path: '/products/riskintel',
        title: 'RiskIntel by Cloudastra: Audit-Grade Compliance Screening for India',
        description:
            'Audit-grade compliance screening for India: 13 Indian regulators, 300K+ entities, live eCourts and GSTIN verification and 4-hop director network mapping.',
        category: 'BusinessApplication',
        keywords: ['kyc', 'aml', 'sanction', 'screening', 'due diligence', 'risk management', 'pep'],
    },
    revinu: {
        key: 'revinu',
        name: 'Revinu',
        path: '/products/revinu',
        title: 'Revinu: AI-Powered Payment Recovery and Revenue Optimization | Cloudastra',
        description:
            'Intelligent payment recovery platform that detects failed payments, categorizes root causes and triggers smart retries automatically to recover revenue that would otherwise be lost.',
        category: 'FinanceApplication',
        keywords: ['failed payment', 'dunning', 'subscription', 'revenue', 'payment retry', 'churn', 'billing'],
    },
    mulenet: {
        key: 'mulenet',
        name: 'MuleNet',
        path: '/products/mulenet',
        title: 'MuleNet: Real-Time Mule Account and Ring Detection for Banks | Cloudastra',
        description:
            'Real-time transaction scoring and graph-native mule ring detection for banks and fintechs, with a regulator-ready evidence pack your Principal Officer can file in minutes.',
        category: 'FinanceApplication',
        keywords: ['mule', 'money laundering', 'pmla', 'aml', 'fraud ring', 'graph analytics'],
    },
    compliance: {
        key: 'compliance',
        name: 'Cloudastra Compliance',
        path: '/products/compliance',
        title: 'Compliance Automation: SOC 2, ISO 27001 and PCI DSS Audit Readiness | Cloudastra',
        description:
            'Compliance automation that gets SaaS and fintech teams audit-ready faster for SOC 2, ISO 27001 and PCI DSS, with automated evidence collection, role-based access and auditor collaboration.',
        category: 'BusinessApplication',
        keywords: ['soc 2', 'soc2', 'iso 27001', 'pci dss', 'gdpr', 'hipaa', 'compliance', 'compliance audit'],
    },
    iPreferAir: {
        key: 'iPreferAir',
        name: 'iPreferAir',
        path: '/products/b2b-travel',
        title: 'iPreferAir: B2B Booking Engine and Travel Management Software | Cloudastra',
        description:
            'B2B travel booking platform with real-time flight, hotel and tour inventory, advanced management tools and seamless operations for travel agencies worldwide.',
        category: 'BusinessApplication',
        keywords: ['travel', 'booking', 'flight', 'hotel', 'gds', 'airline', 'tour'],
    },
    integraAi: {
        key: 'integraAi',
        name: 'Integra-AI',
        path: '/products/integra-ai',
        title: 'Integra-AI: Unified AI Chat Platform for Enterprises | Cloudastra',
        description:
            'Unified AI chat platform with a clean, conversation-style interface. Bring your own API keys and connect to leading models such as GPT-4, Claude and DeepSeek in one place.',
        category: 'BusinessApplication',
        keywords: ['llm', 'chatgpt', 'gpt', 'claude', 'openai', 'rag', 'ai assistant', 'prompt', 'generative ai', 'ai agent'],
    },
    nigmabot: {
        key: 'nigmabot',
        name: 'Nigmabot AI',
        path: '/products/nigmabot-ai',
        title: 'Nigmabot AI: All-in-One AI Productivity Bot | Cloudastra',
        description:
            'All-in-one AI productivity bot for PDFs, content and code that simplifies task management and helps teams stay focused.',
        category: 'BusinessApplication',
        keywords: ['pdf', 'productivity', 'chatbot', 'ai bot', 'automation'],
    },
    medAi: {
        key: 'medAi',
        name: 'MedAI',
        url: 'https://medai.cloudastra.co/',
        title: 'MedAI: AI-Powered Medical Diagnosis Support | Cloudastra',
        description:
            'AI-powered medical diagnosis support that combines intelligent architecture with compassionate care for better healthcare outcomes.',
        category: 'MedicalApplication',
        keywords: ['healthcare', 'medical', 'diagnosis', 'clinical', 'hospital', 'patient'],
    },
    aquaflowz: {
        key: 'aquaflowz',
        name: 'Aquaflowz',
        url: 'https://aquaflowz.cloudastra.co/',
        title: 'Aquaflowz: AI-Powered Water Quality Monitoring | Cloudastra',
        description:
            'AI-powered water quality monitoring and management with real-time analysis, predictive insights and automated alerting for safe, clean water.',
        category: 'UtilitiesApplication',
        keywords: ['water', 'iot', 'sensor', 'environment', 'utility'],
    },
}

export const PRODUCT_LIST = Object.values(PRODUCTS)

export const productHref = (p) => p.url || `${SITE_URL}${p.path}`

/*
 * Products relevant to a blog post, by keyword overlap with its title, tags and categories.
 * Returns up to `limit` products, most matches first; empty when nothing matches, so the
 * caller can fall back to a generic /products link rather than forcing a bad match.
 */
const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
// Whole-word match, so 'aml' does not hit "YAML" or 'rag' hit "storage".
const matchesWord = (haystack, keyword) =>
    new RegExp(`(^|[^a-z0-9])${escapeRegExp(keyword)}(?![a-z0-9])`, 'i').test(haystack)

export function relatedProducts({ title = '', tags = [], categories = [] }, limit = 2) {
    const haystack = [title, ...tags, ...categories].join(' ').toLowerCase()
    return PRODUCT_LIST.map((p) => ({
        product: p,
        score: p.keywords.filter((k) => matchesWord(haystack, k)).length,
    }))
        .filter((m) => m.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, limit)
        .map((m) => m.product)
}
